import React, { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react";
import {
  Slideout,
  TextField,
  Button,
  Icon,
  TextDescription,
} from "@ann/web-components";
import {
  StyledForm,
} from "./ConfigureScheduleLinkModal.styled";
import {
  VALIDATION_PATTERNS,
} from "../../../utils/enums";
import { useStore } from "../../../hooks";
const ConfigureScheduleLinkModal = observer(({
  isOpen,
  onClose,
  onTouched,
  onSave,
  configureActionName,
  showBackButton = false,
  onBack,
}) => {
  const { editAgent } = useStore();
  const [formData, setFormData] = useState({
    schedulingLink: "",
    linkName: "",
    description: "",
  });
  const [isUrlInvalid, setIsUrlInvalid] = useState(false);
  const [isLinkNameInvalid, setIsLinkNameInvalid] = useState(false);

  const modalLabels = {
    appointment_schedule: {
      title: "Configure Your Schedule Link",
      linkName: "Link Name",
      linkNamePlaceholder: "Give your link a clear name to recognize it easily",
      schedulingLink: "Scheduling Link",
      schedulingLinkPlaceholder: "Paste your scheduling link",
      description: "Description (Optional)",
      descriptionPlaceholder: "Add any additional details or notes",
    },
    repair_schedule: {
      title: "Configure Repair Order",
      linkName: "Order Name",
      linkNamePlaceholder: "Give repair order a recognizable name",
      schedulingLink: "Repair Scheduling Link",
      schedulingLinkPlaceholder:
        "Paste the scheduling link for this repair order",
      description: "Description (Optional)",
      descriptionPlaceholder: "Add any additional details or notes",
    },
  };

  useEffect(() => {
    if (isOpen && configureActionName) {
      const actionSettings = editAgent.actionsSettings.find(
        (setting) => setting[configureActionName]
      );
      
      if (actionSettings) {
        setFormData({
          schedulingLink: actionSettings[configureActionName].link || "",
          linkName: actionSettings[configureActionName].name || "",
          description: actionSettings[configureActionName].description || "",
        });
      } else {
        setFormData({
          schedulingLink: "",
          linkName: "",
          description: "",
        });
      }
    }
  }, [isOpen, configureActionName, editAgent.actionsSettings]);

  const isValidUrl = (url) => {
    return VALIDATION_PATTERNS.SCHEDULING_URL.pattern.test(url);
  };

  const handleSave = useCallback(() => {
    if (!formData.schedulingLink || !formData.schedulingLink.trim()) {
      setIsUrlInvalid(true);
      return;
    }

    if (!formData.linkName || !formData.linkName.trim()) {
      setIsLinkNameInvalid(true);  
      return;
    }

    if (formData.schedulingLink && !isValidUrl(formData.schedulingLink)) {
      setIsUrlInvalid(true);
      return;
    }

    editAgent.onChangeActionsSettings(
      {
        [configureActionName]: {
          name: formData.linkName,
          description: formData.description,
          link: formData.schedulingLink,
        },
      },
      configureActionName
    );

    setIsUrlInvalid(false);
    setIsLinkNameInvalid(false);
    
    onSave?.();
  }, [formData, configureActionName, editAgent, onSave]);

  const handleClose = useCallback(() => {
    setFormData({
      schedulingLink: "",
      linkName: "",
      description: "",
    });
    setIsUrlInvalid(false);
    setIsLinkNameInvalid(false);
    onClose?.();
  }, [onClose]);


  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    onTouched?.();
    
    if (field === 'schedulingLink') {
      setIsUrlInvalid(false);
    }
    if (field === 'linkName') {
      setIsLinkNameInvalid(false);
    }
  }, [onTouched]);

  if (!configureActionName) return null;

  return (
    <Slideout open={isOpen} onClose={handleClose} size="medium">
      {showBackButton && (
        <Slideout.HeaderActions>
          <Icon name='arrowLeft' size='medium' onClick={onBack} />
        </Slideout.HeaderActions>
      )}
      <Slideout.Header>
        <Slideout.Title value={modalLabels[configureActionName]?.title || 'Configure'} />
      </Slideout.Header>

      <Slideout.Content>
        <StyledForm>
          <TextField
            value={formData.schedulingLink}
            onChange={(value) => handleInputChange("schedulingLink", value)}
            placeholder={modalLabels[configureActionName]?.schedulingLinkPlaceholder}
            size="medium"
            label={modalLabels[configureActionName]?.schedulingLink}
            required
            fullWidth
            state={isUrlInvalid ? "error" : "default"}
          />

          <TextField
            value={formData.linkName}
            onChange={(value) => handleInputChange("linkName", value)}
            placeholder={modalLabels[configureActionName]?.linkNamePlaceholder}
            size="medium"
            label={modalLabels[configureActionName]?.linkName}
            required
            fullWidth
            state={isLinkNameInvalid ? "error" : "default"}
          />

          <TextDescription
            value={formData.description}
            onChange={(value) => handleInputChange("description", value)}
            placeholder={modalLabels[configureActionName]?.descriptionPlaceholder}
            size="medium"
            label={modalLabels[configureActionName]?.description}
            rows={4}
            fullWidth
            maxLength={300}
            showCharacterCount
          />
        </StyledForm>
      </Slideout.Content>
      
      <Slideout.Actions>
        <Button
          label="Cancel"
          size="medium"
          type="secondary"
          onClick={handleClose}
        />
        <Button
          label="Save"
          size="medium"
          type="primary"
          onClick={handleSave}
        />
      </Slideout.Actions>
    </Slideout>
  );
});

export default ConfigureScheduleLinkModal;
